/**
 * Project timeline (Gantt) — 2026-09-27.
 *
 * Bars run from a task's start date to its due date; milestones are
 * diamonds; arrows are finish-to-start dependencies. Tasks on the critical
 * path (zero slack) are highlighted, and links whose dates break the
 * dependency are listed as conflicts. Colours come from theme tokens.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, CalendarRange, Link2, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import {
  addDays,
  daysBetween,
  timelineService,
  type ProjectTimeline,
  type TimelineTask,
} from '../../services/timeline'
import type { ApiErrorLike } from '../../utils/apiError'

type Zoom = 'day' | 'week' | 'month'
const PX_PER_DAY: Record<Zoom, number> = { day: 28, week: 10, month: 3.5 }
const ROW_H = 36
const HEADER_H = 40
const NAME_W = 240

interface Props {
  projectId?: number
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

function formatDay(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' })
}

export function TimelineSection({ projectId }: Props) {
  const [data, setData] = useState<ProjectTimeline | null>(null)
  const [loading, setLoading] = useState(false)
  const [zoom, setZoom] = useState<Zoom>('week')
  const [fromId, setFromId] = useState<string>('')
  const [toId, setToId] = useState<string>('')
  const [saving, setSaving] = useState(false)

  const load = useCallback(() => {
    if (!projectId) return
    setLoading(true)
    timelineService
      .get(projectId)
      .then(setData)
      .catch(() => {
        setData(null)
        toast.error('Could not load the timeline. Refresh to try again.')
      })
      .finally(() => setLoading(false))
  }, [projectId])

  useEffect(load, [load])

  const scheduled = useMemo(() => (data?.tasks ?? []).filter((t) => t.scheduled), [data])
  const unscheduled = useMemo(() => (data?.tasks ?? []).filter((t) => !t.scheduled), [data])

  // Visible range: data range padded by 3 days each side, and always
  // including today so "where are we" is visible.
  const range = useMemo(() => {
    if (!data?.range_start || !data.range_end) return null
    const today = todayIso()
    const start = addDays([data.range_start, today].sort()[0], -3)
    const end = addDays([data.range_end, today].sort()[1], 3)
    return { start, end, days: daysBetween(start, end) + 1 }
  }, [data])

  const pxPerDay = PX_PER_DAY[zoom]
  const width = range ? Math.ceil(range.days * pxPerDay) : 0
  const x = (iso: string) => (range ? daysBetween(range.start, iso) * pxPerDay : 0)
  const rowIndex = useMemo(() => new Map(scheduled.map((t, i) => [t.id, i])), [scheduled])

  const ticks = useMemo(() => {
    if (!range) return [] as string[]
    const step = zoom === 'day' ? 1 : zoom === 'week' ? 7 : 30
    const out: string[] = []
    for (let d = 0; d < range.days; d += step) out.push(addDays(range.start, d))
    return out
  }, [range, zoom])

  const addLink = async () => {
    if (!projectId || !fromId || !toId) return
    setSaving(true)
    try {
      await timelineService.addDependency(projectId, Number(fromId), Number(toId))
      toast.success('Dependency added')
      setFromId('')
      setToId('')
      load()
    } catch (errRaw) {
      const err = errRaw as ApiErrorLike
      toast.error(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Could not add the dependency.')
    } finally {
      setSaving(false)
    }
  }

  const removeLink = async (id: number) => {
    if (!projectId) return
    try {
      await timelineService.removeDependency(projectId, id)
      toast.success('Dependency removed')
      load()
    } catch {
      toast.error('Could not remove the dependency.')
    }
  }

  const titleOf = (id: number) => data?.tasks.find((t) => t.id === id)?.title ?? `Task ${id}`

  if (!projectId) {
    return <p className="text-sm text-muted-foreground">Open a project to see its timeline.</p>
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
            <CalendarRange className="h-5 w-5" /> Timeline
          </h2>
          <p className="text-sm text-muted-foreground">
            Bars run from start to due date. Highlighted bars are on the critical path: any delay there moves the finish date
            {data?.project_finish ? ` (${formatDay(data.project_finish)})` : ''}.
          </p>
        </div>
        <div className="flex rounded-lg border border-border bg-card p-1" role="group" aria-label="Zoom">
          {(['day', 'week', 'month'] as Zoom[]).map((z) => (
            <button
              key={z}
              type="button"
              onClick={() => setZoom(z)}
              aria-pressed={zoom === z}
              className={`rounded-md px-3 py-1 text-sm capitalize ${zoom === z ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}
            >
              {z}
            </button>
          ))}
        </div>
      </div>

      {loading && <p className="text-sm text-muted-foreground">Loading timeline…</p>}

      {!loading && data && scheduled.length === 0 && (
        <div className="rounded-lg border border-dashed border-border bg-card p-6 text-sm text-muted-foreground">
          No task has a start or due date yet. Add dates to tasks and they appear here as bars.
        </div>
      )}

      {data && data.conflicts.length > 0 && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm" role="status">
          <p className="flex items-center gap-2 font-medium text-destructive">
            <AlertTriangle className="h-4 w-4" /> {data.conflicts.length} schedule conflict{data.conflicts.length > 1 ? 's' : ''}
          </p>
          <ul className="mt-1 list-disc pl-6 text-foreground">
            {data.conflicts.map((c) => (
              <li key={`${c.predecessor_id}-${c.successor_id}`}>
                “{titleOf(c.successor_id)}” starts {c.days_early} day{c.days_early > 1 ? 's' : ''} before “{titleOf(c.predecessor_id)}” finishes.
                Move its start to {formatDay(c.earliest_allowed_start)} or later.
              </li>
            ))}
          </ul>
        </div>
      )}

      {range && scheduled.length > 0 && (
        <div className="flex overflow-hidden rounded-lg border border-border bg-card">
          {/* Task names */}
          <div className="shrink-0 border-r border-border" style={{ width: NAME_W }}>
            <div className="flex items-end border-b border-border px-3 pb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground" style={{ height: HEADER_H }}>
              Task
            </div>
            {scheduled.map((t) => (
              <div key={t.id} className="flex items-center gap-2 truncate border-b border-border/60 px-3 text-sm text-foreground" style={{ height: ROW_H }} title={t.title}>
                {t.critical && <span className="h-2 w-2 shrink-0 rounded-full bg-accent" aria-label="On critical path" />}
                <span className="truncate">{t.title}</span>
              </div>
            ))}
          </div>

          {/* Chart */}
          <div className="overflow-x-auto">
            <svg width={width} height={HEADER_H + scheduled.length * ROW_H} role="img" aria-label="Project timeline chart">
              <defs>
                <marker id="tl-arrow" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto">
                  <path d="M0,0 L8,4 L0,8 z" fill="var(--muted-foreground)" />
                </marker>
              </defs>
              {ticks.map((iso) => (
                <g key={iso}>
                  <line x1={x(iso)} x2={x(iso)} y1={HEADER_H - 6} y2={HEADER_H + scheduled.length * ROW_H} stroke="var(--border)" strokeWidth={1} />
                  <text x={x(iso) + 4} y={HEADER_H - 12} fontSize={11} fill="var(--muted-foreground)">{formatDay(iso)}</text>
                </g>
              ))}
              {/* Today */}
              <line x1={x(todayIso())} x2={x(todayIso())} y1={HEADER_H - 6} y2={HEADER_H + scheduled.length * ROW_H} stroke="var(--destructive)" strokeDasharray="4 3" strokeWidth={1.5} />

              {/* Dependency arrows: predecessor end → successor start */}
              {data?.dependencies.map((d) => {
                const a = rowIndex.get(d.predecessor_id)
                const b = rowIndex.get(d.successor_id)
                const pa = scheduled[a ?? -1]
                const pb = scheduled[b ?? -1]
                if (a === undefined || b === undefined || !pa?.end || !pb?.start) return null
                const x1 = x(pa.end) + pxPerDay
                const y1 = HEADER_H + a * ROW_H + ROW_H / 2
                const x2 = x(pb.start)
                const y2 = HEADER_H + b * ROW_H + ROW_H / 2
                const mid = Math.max(x1 + 8, Math.min(x2 - 8, x1 + 8))
                return (
                  <path key={d.id} d={`M${x1},${y1} H${mid} V${y2} H${x2}`} fill="none" stroke="var(--muted-foreground)" strokeWidth={1.2} markerEnd="url(#tl-arrow)" />
                )
              })}

              {/* Bars */}
              {scheduled.map((t: TimelineTask, i) => {
                const y = HEADER_H + i * ROW_H + 8
                const bx = x(t.start!)
                const bw = Math.max((daysBetween(t.start!, t.end!) + 1) * pxPerDay, 6)
                const done = t.status.toLowerCase().includes('done') || t.status.toLowerCase().includes('complete')
                return (
                  <g key={t.id}>
                    <rect x={bx} y={y} width={bw} height={ROW_H - 16} rx={4}
                      fill={t.critical ? 'var(--accent)' : 'var(--primary)'} opacity={done ? 0.45 : 1} />
                    <title>{`${t.title}: ${formatDay(t.start!)} – ${formatDay(t.end!)}${t.critical ? ' · critical path' : t.slack_days ? ` · ${t.slack_days} days of slack` : ''}`}</title>
                  </g>
                )
              })}

              {/* Milestones */}
              {data?.milestones.filter((m) => m.due_date).map((m) => {
                const mx = x(m.due_date!) + pxPerDay / 2
                return (
                  <g key={`m-${m.id}`}>
                    <path d={`M${mx},${HEADER_H - 4} l6,-6 l6,6 l-6,6 z`} transform="translate(-6,0)" fill="var(--karyva-midnight)" />
                    <title>{`Milestone: ${m.name} (${formatDay(m.due_date!)})`}</title>
                  </g>
                )
              })}
            </svg>
          </div>
        </div>
      )}

      {data && scheduled.length > 1 && (
        <div className="rounded-lg border border-border bg-card p-4">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground"><Link2 className="h-4 w-4" /> Dependencies</h3>
          <div className="mt-3 flex flex-wrap items-end gap-2">
            <label className="text-sm">
              <span className="mb-1 block text-muted-foreground">This task must finish</span>
              <select id="tl-dep-from" value={fromId} onChange={(e) => setFromId(e.target.value)} className="rounded-md border border-border bg-background px-2 py-1.5 text-sm">
                <option value="">Choose a task…</option>
                {data.tasks.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
              </select>
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-muted-foreground">before this one can start</span>
              <select id="tl-dep-to" value={toId} onChange={(e) => setToId(e.target.value)} className="rounded-md border border-border bg-background px-2 py-1.5 text-sm">
                <option value="">Choose a task…</option>
                {data.tasks.filter((t) => String(t.id) !== fromId).map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
              </select>
            </label>
            <button type="button" onClick={addLink} disabled={!fromId || !toId || saving}
              className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50">
              {saving ? 'Adding…' : 'Add dependency'}
            </button>
          </div>
          {data.dependencies.length > 0 && (
            <ul className="mt-3 divide-y divide-border text-sm">
              {data.dependencies.map((d) => (
                <li key={d.id} className="flex items-center justify-between py-2">
                  <span>“{titleOf(d.predecessor_id)}” → “{titleOf(d.successor_id)}”{d.lag_days ? ` (+${d.lag_days} days)` : ''}</span>
                  <button type="button" onClick={() => removeLink(d.id)} className="rounded p-1 text-muted-foreground hover:text-destructive" aria-label="Remove dependency">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {unscheduled.length > 0 && (
        <p className="text-sm text-muted-foreground">
          {unscheduled.length} task{unscheduled.length > 1 ? 's have' : ' has'} no dates and {unscheduled.length > 1 ? 'are' : 'is'} not shown: {unscheduled.slice(0, 5).map((t) => t.title).join(', ')}{unscheduled.length > 5 ? '…' : ''}
        </p>
      )}
    </div>
  )
}
